import { Board } from './Board';
import { RuleValidator } from './RuleValidator';
import { createGameFromSGF, createSGF } from '../utils/sgf';
import { estimateScore, findDeadStones } from '../utils/score';
import { StoneColor, GameStatus, GameResultReason, MoveType } from '../types';
/** 让子星位坐标（1-based），取前 n 个（1-9） */
export function getHandicapPoints(size, n) {
    const offset = size === 9 ? 3 : 4; // 边星位（1-based）
    const center = Math.floor(size / 2) + 1;
    const far = size - offset + 1;
    const order = [
        { x: offset, y: offset }, // 左上
        { x: far, y: far }, // 右下
        { x: far, y: offset }, // 右上
        { x: offset, y: far }, // 左下
        { x: center, y: center }, // 天元
        { x: offset, y: center }, // 左中
        { x: far, y: center }, // 右中
        { x: center, y: offset }, // 上中
        { x: center, y: far }, // 下中
    ];
    return order.slice(0, Math.max(0, Math.min(n, 9)));
}
/**
 * 围棋游戏管理类
 * 负责对局的完整管理
 */
export class GoGame {
    constructor(settings) {
        this.timeRemaining = new Map();
        this.byoyomiPeriods = new Map();
        this.moves = [];
        this.settings = settings;
        this.board = new Board(settings.boardSize);
        // 预置让子（在 validator 创建前，确保验证器从让子局面开始）
        if (this.settings.handicap > 0) {
            for (const p of getHandicapPoints(this.settings.boardSize, this.settings.handicap)) {
                this.board.placeStone(p, StoneColor.BLACK);
            }
        }
        this.validator = new RuleValidator(this.board);
        this.state = {
            board: this.board.getState(),
            // 让子棋白先
            currentPlayer: this.settings.handicap > 0 ? StoneColor.WHITE : StoneColor.BLACK,
            consecutivePasses: 0,
            status: GameStatus.WAITING,
            startTime: Date.now()
        };
        this.initializeTime();
    }
    /**
     * 初始化计时器
     */
    initializeTime() {
        this.timeRemaining.set(StoneColor.BLACK, this.settings.timeSettings.mainTime);
        this.timeRemaining.set(StoneColor.WHITE, this.settings.timeSettings.mainTime);
        if (this.settings.timeSettings.type === 'byoyomi') {
            this.byoyomiPeriods.set(StoneColor.BLACK, this.settings.timeSettings.byoyomiPeriods || 0);
            this.byoyomiPeriods.set(StoneColor.WHITE, this.settings.timeSettings.byoyomiPeriods || 0);
        }
    }
    /**
     * 开始对局
     */
    start() {
        if (this.state.status !== GameStatus.WAITING) {
            throw new Error('对局已开始或已结束');
        }
        this.state.status = GameStatus.PLAYING;
        this.state.startTime = Date.now();
    }
    /**
     * 落子
     */
    playMove(coord, color) {
        if (this.state.status !== GameStatus.PLAYING) {
            throw new Error('对局未开始或已结束');
        }
        if (color !== this.state.currentPlayer) {
            throw new Error('不是该玩家的回合');
        }
        // 验证棋步
        const validation = this.validator.validateMove(coord, color);
        if (!validation.isValid) {
            throw new Error(validation.error);
        }
        // 执行棋步
        const result = this.validator.executeMove(coord, color);
        // 同步棋盘状态（executeMove 已在验证器内部前进）
        this.board = this.validator.getBoard();
        // 创建棋步记录
        const move = {
            coord,
            color,
            type: coord === null ? MoveType.PASS : MoveType.NORMAL,
            capturedStones: result.capturedStones,
            timestamp: Date.now()
        };
        // 记录棋步
        this.moves.push(move);
        // 更新游戏状态
        this.updateGameState(move, result);
        return result;
    }
    /**
     * 更新游戏状态
     */
    updateGameState(move, executeResult) {
        // 更新连续pass次数
        if (move.type === MoveType.PASS) {
            this.state.consecutivePasses++;
        }
        else {
            this.state.consecutivePasses = 0;
        }
        // 切换玩家
        this.state.currentPlayer = this.getOpponentColor(move.color);
        // 检查是否终局
        if (this.shouldEndGame()) {
            this.endGame(GameResultReason.COUNTING);
        }
    }
    /**
     * 认输
     */
    resign(color) {
        if (this.state.status !== GameStatus.PLAYING) {
            throw new Error('对局未开始或已结束');
        }
        const winner = color === StoneColor.BLACK ? StoneColor.WHITE : StoneColor.BLACK;
        this.endGame(GameResultReason.RESIGN, winner);
    }
    /**
     * 请求和棋（直接判和棋，无胜者）
     */
    requestDraw(color) {
        if (this.state.status !== GameStatus.PLAYING) {
            throw new Error('对局未开始或已结束');
        }
        this.endGame(GameResultReason.DRAW);
        return true;
    }
    /**
     * 悔棋：回退最后一步棋
     */
    undo() {
        if (this.moves.length === 0) {
            return false;
        }
        // 移除最后一步
        this.moves.pop();
        // 重建局面（含劫争/全局同形历史），保证回退后规则状态一致
        this.rebuildFromMoves();
        // 回到可对局状态
        this.state.status = GameStatus.PLAYING;
        this.state.result = undefined;
        this.state.endTime = undefined;
        return true;
    }
    /**
     * 从当前棋步序列重建棋盘与验证器状态
     * 用于悔棋后恢复劫争、全局同形再现等规则上下文
     */
    rebuildFromMoves() {
        this.board = new Board(this.settings.boardSize);
        this.validator = new RuleValidator(this.board);
        this.state.currentPlayer = StoneColor.BLACK;
        this.state.consecutivePasses = 0;
        for (const move of this.moves) {
            this.validator.executeMove(move.coord, move.color);
            this.board = this.validator.getBoard();
            if (move.type === MoveType.PASS) {
                this.state.consecutivePasses++;
            }
            else {
                this.state.consecutivePasses = 0;
            }
            this.state.currentPlayer = this.getOpponentColor(move.color);
        }
        this.state.board = this.board.getState();
    }
    /**
     * 暂停对局
     */
    pause() {
        if (this.state.status !== GameStatus.PLAYING) {
            throw new Error('只有进行中的对局可以暂停');
        }
        this.state.status = GameStatus.PAUSED;
    }
    /**
     * 恢复对局
     */
    resume() {
        if (this.state.status !== GameStatus.PAUSED) {
            throw new Error('只有暂停的对局可以恢复');
        }
        this.state.status = GameStatus.PLAYING;
    }
    /**
     * 结束对局
     */
    endGame(reason, winner) {
        this.state.status = GameStatus.FINISHED;
        this.state.endTime = Date.now();
        // 计算胜负
        const result = this.calculateResult(reason, winner);
        this.state.result = result;
    }
    /**
     * 计算对局结果
     */
    calculateResult(reason, winner) {
        if (reason === GameResultReason.DRAW) {
            return {
                winner: null,
                reason,
                blackScore: 0,
                whiteScore: 0,
                margin: 0
            };
        }
        if (winner) {
            // 认输或超时
            return {
                winner,
                reason,
                blackScore: 0,
                whiteScore: 0,
                margin: 0
            };
        }
        // 数目计算（中国规则数子法）
        const scores = this.calculateScore();
        // 黑贴 3.75 子（= 7.5 目）
        const komiStones = this.settings.komi / 2;
        const rawMargin = scores.black - scores.white - komiStones;
        let resultWinner;
        let margin = 0;
        if (rawMargin > 0) {
            resultWinner = StoneColor.BLACK;
            margin = rawMargin;
        }
        else if (rawMargin < 0) {
            resultWinner = StoneColor.WHITE;
            margin = -rawMargin;
        }
        else {
            resultWinner = null; // 和棋
        }
        return {
            winner: resultWinner,
            reason: GameResultReason.COUNTING,
            blackScore: scores.black,
            whiteScore: scores.white,
            margin
        };
    }
    /**
     * 计算数目（中国规则数子法）
     * 得分 = 己方活子数 + 己方围住的空点（领地）
     * 注意：死子判定为简化实现，终局时假设盘面已无死子（有气但已死的块暂不自动识别）
     */
    calculateScore() {
        // 终局按中国规则数子：先自动识别死子提掉，再数（粗略启发式兜底）
        const stones = this.board.getState().stones;
        return estimateScore(stones, findDeadStones(stones));
    }
    /**
     * 检查是否应该终局
     */
    shouldEndGame() {
        // 双方连续pass
        if (this.state.consecutivePasses >= 2) {
            return true;
        }
        // TODO: 添加其他终局条件
        return false;
    }
    /**
     * 获取对手颜色
     */
    getOpponentColor(color) {
        return color === StoneColor.BLACK ? StoneColor.WHITE : StoneColor.BLACK;
    }
    /**
     * 获取游戏状态
     */
    getGameState() {
        return { ...this.state };
    }
    /**
     * 获取游戏设置
     */
    getGameSettings() {
        return { ...this.settings };
    }
    /**
     * 获取棋盘状态
     */
    getBoardState() {
        return this.board.getState();
    }
    /**
     * 获取当前玩家
     */
    getCurrentPlayer() {
        return this.state.currentPlayer;
    }
    /**
     * 获取剩余时间
     */
    getTimeRemaining(color) {
        return this.timeRemaining.get(color) || 0;
    }
    /**
     * 获取读秒次数
     */
    getByoyomiPeriods(color) {
        return this.byoyomiPeriods.get(color) || 0;
    }
    /**
     * 验证棋步（不执行）
     */
    validateMove(coord, color) {
        return this.validator.validateMove(coord, color);
    }
    /**
     * 获取所有合法着法
     */
    getLegalMoves(color) {
        const legalMoves = [];
        const size = this.settings.boardSize;
        for (let x = 1; x <= size; x++) {
            for (let y = 1; y <= size; y++) {
                const coord = { x, y };
                const validation = this.validator.validateMove(coord, color);
                if (validation.isValid) {
                    legalMoves.push(coord);
                }
            }
        }
        // PASS总是合法的
        legalMoves.push(null); // 类型处理
        return legalMoves;
    }
    /**
     * 导出为SGF格式
     */
    toSGF() {
        return createSGF(this.moves, {
            boardSize: this.settings.boardSize,
            komi: this.settings.komi,
            handicap: this.settings.handicap
        });
    }
    /**
     * 从SGF格式导入
     */
    loadFromSGF(sgf) {
        const game = createGameFromSGF(sgf);
        // 按棋谱尺寸重建棋盘与验证器
        this.settings.boardSize = game.boardSize;
        this.settings.komi = game.komi;
        this.settings.handicap = game.handicap;
        this.board = new Board(game.boardSize);
        // 预置让子
        if (this.settings.handicap > 0) {
            for (const p of getHandicapPoints(this.settings.boardSize, this.settings.handicap)) {
                this.board.placeStone(p, StoneColor.BLACK);
            }
        }
        this.validator = new RuleValidator(this.board);
        this.moves = [];
        this.state = {
            board: this.board.getState(),
            currentPlayer: this.settings.handicap > 0 ? StoneColor.WHITE : StoneColor.BLACK,
            consecutivePasses: 0,
            status: GameStatus.WAITING,
            startTime: Date.now()
        };
        this.initializeTime();
        // 逐手重放棋谱（正确处理提子/劫争）
        this.start();
        for (const move of game.moves) {
            this.playMove(move.coord, move.color);
        }
    }
    /**
     * 重置游戏
     */
    reset() {
        this.board = new Board(this.settings.boardSize);
        // 预置让子
        if (this.settings.handicap > 0) {
            for (const p of getHandicapPoints(this.settings.boardSize, this.settings.handicap)) {
                this.board.placeStone(p, StoneColor.BLACK);
            }
        }
        this.validator = new RuleValidator(this.board);
        this.moves = [];
        this.state = {
            board: this.board.getState(),
            currentPlayer: this.settings.handicap > 0 ? StoneColor.WHITE : StoneColor.BLACK,
            consecutivePasses: 0,
            status: GameStatus.WAITING,
            startTime: Date.now()
        };
        this.initializeTime();
    }
}
//# sourceMappingURL=GoGame.js.map